import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Trash2, Plus, CheckSquare, Square } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AgentInsights } from '@/components/AgentInsights';
import { TimelinePanel } from '@/components/TimelinePanel';
import { StageAdvanceModal } from '@/components/StageAdvanceModal';
import { RequirePermission } from '@/components/RequirePermission';
import { OpportunityCreateDemoSheet } from '@/pages/opportunities/OpportunityCreateDemoSheet';
import { listDemosByOpportunity, type Demo } from '@/api/demos';
import { CloseReasonPicker, type CloseCategory } from '@/components/CloseReasonPicker';
import { AttachmentPanel } from '@/components/AttachmentPanel';
import { SnapshotPanel } from '@/components/SnapshotPanel';
import { useAuthStore } from '@/stores/authStore';

interface LineItem {
  id: string;
  revenueType: string;
  description: string | null;
  quantity: number;
  unitPrice: number;
  recurrence: string;
  recurrenceMonths: number;
  totalValue: number;
}

interface CostItem {
  id: string;
  costCategory: string;
  description: string;
  amount: number;
  quantity: number;
  recurrence: string;
  recurrenceMonths: number;
  totalValue: number;
  vendor: string | null;
}

const REVENUE_TYPES = ['LICENSE', 'SUBSCRIPTION', 'SERVICE', 'SUPPORT', 'RESALE'] as const;
const LINE_RECURRENCES = ['ONE_TIME', 'MONTHLY', 'ANNUAL', 'MULTI_YEAR'] as const;
const COST_CATEGORIES = ['PURCHASE', 'CONTRACTING', 'RECURRING', 'INDIRECT', 'RISK'] as const;
const COST_RECURRENCES = ['ONE_TIME', 'ANNUAL', 'MONTHLY'] as const;

interface StageTransition {
  fromStageId: string | null;
  toStageId: string;
  actorId: string | null;
  direction: string;
  reason: string | null;
  occurredAt: string | null;
}

interface Summary {
  id: string;
  name: string;
  status: string;
  progressPercent: string;
  estimatedDealValue: string;
  currency: string;
  forecastClassification: string;
  expectedCloseDate: string | null;
  templateId: string;
  currentStageId: string;
  accountId: string | null;
  primaryContactId: string | null;
  assignedRepId: string | null;
  competitorNames: string[];
  // 00262 MEDDIC + AI fields
  meddicMetrics: string | null;
  meddicDecisionProcess: string | null;
  meddicPaperProcess: string | null;
  aiHealthScore: number | null;
  aiRiskFlags: string[];
  aiCloseProbability: number | null;
}

interface OpportunityContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  title: string | null;
}

interface OpportunityDetail {
  summary: Summary;
  accountName: string | null;
  contactName: string | null;
  campaignName: string | null;
  templateName: string | null;
  currentStageName: string | null;
  needsStageReview: boolean;
  stageHistory: StageTransition[];
}

interface Stage {
  id: string;
  order: number;
  name: string;
  percentageWeight: string;
  isGate: boolean;
}

// Story 76.1
interface OpenOpportunity {
  id: string;
  name: string;
  estimatedDealValue: string;
  currency: string;
  forecastClassification: string;
}

interface PreMeetingBrief {
  opportunityId: string;
  opportunityName: string;
  accountName: string | null;
  dealValue: string;
  currency: string;
  forecastClassification: string;
  currentStageName: string | null;
  expectedCloseDate: string | null;
  openOpportunities: OpenOpportunity[];
  recentCommunications: string[];
  talkingPoints: string[];
}

// Story 76.2
interface MeetingSummaryResult {
  meetingLogId: string;
  summary: string;
  actionItems: string[];
  suggestedNextAction: string;
  tasksCreated: number;
}

const MEETING_TYPES = ['DISCOVERY', 'DEMO', 'QBR', 'NEGOTIATION', 'CLOSING', 'FOLLOW_UP', 'OTHER'] as const;

interface PlaybookStep {
  title: string;
  description?: string;
  suggestedAction?: string;
}

interface PlaybookProgress {
  stepIndex: number;
  checked: boolean;
  checkedAt: string | null;
}

interface PlaybookWithProgress {
  id: string;
  name: string;
  triggerKind: string;
  steps: PlaybookStep[];
  progress: PlaybookProgress[];
}

const FORECAST_BADGE: Record<string, string> = {
  COMMIT: 'bg-emerald-900/40 text-emerald-200',
  BEST_CASE: 'bg-blue-900/40 text-blue-200',
  UPSIDE: 'bg-amber-900/40 text-amber-200',
  PIPELINE: 'bg-muted text-foreground',
};

const STATUS_BADGE: Record<string, string> = {
  OPEN: 'bg-blue-900/40 text-blue-200',
  WON: 'bg-emerald-900/40 text-emerald-200',
  LOST: 'bg-red-900/40 text-red-200',
};

const DIRECTION_LABEL: Record<string, string> = {
  INITIAL: 'Started',
  FORWARD: 'Advanced',
  BACKWARD: 'Moved back',
};

export function OpportunityDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<OpportunityDetail | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [closing, setClosing] = useState<'WON' | 'LOST' | null>(null);
  const [closeReason, setCloseReason] = useState('');
  const [closeReasonCode, setCloseReasonCode] = useState('');
  const [closeReasonCategory, setCloseReasonCategory] = useState<CloseCategory | ''>('');
  const [closeNotes, setCloseNotes] = useState('');
  const [closePrimaryCategory, setClosePrimaryCategory] = useState('');
  const [closePrimarySubcategory, setClosePrimarySubcategory] = useState('');
  const userRole = useAuthStore((s) => s.user?.role);
  const allowDataQuality =
    userRole === 'SALES_MANAGER' ||
    userRole === 'TENANT_ADMIN' ||
    userRole === 'PLATFORM_ADMIN';
  const [advancing, setAdvancing] = useState<{ stageId: string; stageName: string } | null>(null);

  // Contacts of the opportunity's account.
  const [accountContacts, setAccountContacts] = useState<OpportunityContact[]>([]);

  // Story 76.1 — Pre-meeting brief
  const [brief, setBrief] = useState<PreMeetingBrief | null>(null);
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefCopied, setBriefCopied] = useState(false);

  // Story 76.2 — Post-meeting summary
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [meetingType, setMeetingType] = useState('OTHER');
  const [meetingTitle, setMeetingTitle] = useState('');
  const [summaryResult, setSummaryResult] = useState<MeetingSummaryResult | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // Demo sheet state (114.3)
  const [demoSheetOpen, setDemoSheetOpen] = useState(false);

  // Demos tab state (114.6) — demos linked to this opportunity
  const [opportunityDemos, setOpportunityDemos] = useState<Demo[]>([]);

  // Playbook state
  const [playbooks, setPlaybooks] = useState<PlaybookWithProgress[]>([]);

  // Sequence enrollment state (story 61.4)
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [sequences, setSequences] = useState<{ id: string; name: string }[]>([]);
  const [selectedSequenceId, setSelectedSequenceId] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  // Line items state
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [lineOpen, setLineOpen] = useState(true);
  const [newLine, setNewLine] = useState({
    revenueType: 'LICENSE' as string,
    description: '',
    quantity: '1',
    unitPrice: '',
    recurrence: 'ONE_TIME' as string,
  });

  // Cost items state
  const [costItems, setCostItems] = useState<CostItem[]>([]);
  const [costOpen, setCostOpen] = useState(true);
  const [costVisible, setCostVisible] = useState(true);
  const [newCost, setNewCost] = useState({
    costCategory: 'PURCHASE' as string,
    description: '',
    amount: '',
    quantity: '1',
    recurrence: 'ONE_TIME' as string,
    recurrenceMonths: '',
    vendor: '',
  });

  // Inline-edit state — at most one row in edit mode at a time per table.
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editLineDraft, setEditLineDraft] = useState<{
    revenueType: string;
    description: string;
    quantity: string;
    unitPrice: string;
    recurrence: string;
    recurrenceMonths: string;
  } | null>(null);
  const [editingCostId, setEditingCostId] = useState<string | null>(null);
  const [editCostDraft, setEditCostDraft] = useState<{
    costCategory: string;
    description: string;
    amount: string;
    quantity: string;
    recurrence: string;
    recurrenceMonths: string;
    vendor: string;
  } | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await apiClient.get<OpportunityDetail>(
        `/opportunities/${id}`,
      );
      setDetail(data);
      const { data: template } = await apiClient.get<{ stages: Stage[] }>(
        `/process-templates/${data.summary.templateId}`,
      );
      setStages(template.stages.sort((a, b) => a.order - b.order));
      if (data.summary.accountId) {
        const { data: ct } = await apiClient.get<OpportunityContact[]>(
          '/contacts',
          { params: { accountId: data.summary.accountId } },
        );
        setAccountContacts(ct);
      } else {
        setAccountContacts([]);
      }
    } catch {
      setDetail(null);
    }
  }, [id]);

  async function setPrimaryContact(contactId: string | null) {
    if (!id) return;
    try {
      await apiClient.patch(`/opportunities/${id}`, { primaryContactId: contactId });
      load();
    } catch {
      showToast('Failed to update primary contact');
    }
  }

  const loadLineItems = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await apiClient.get<LineItem[]>(`/opportunities/${id}/line-items`);
      setLineItems(data);
    } catch {
      setLineItems([]);
    }
  }, [id]);

  const loadCostItems = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await apiClient.get<CostItem[]>(`/opportunities/${id}/cost-items`);
      setCostItems(data);
      setCostVisible(true);
    } catch {
      setCostItems([]);
      setCostVisible(false);
    }
  }, [id]);

  const loadPlaybooks = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await apiClient.get<PlaybookWithProgress[]>(`/opportunities/${id}/playbooks`);
      setPlaybooks(data);
    } catch {
      setPlaybooks([]);
    }
  }, [id]);

  // 114.6 — Load demos linked to this opportunity
  const loadOpportunityDemos = useCallback(async () => {
    if (!id) return;
    try {
      const demos = await listDemosByOpportunity(id);
      setOpportunityDemos(demos);
    } catch {
      setOpportunityDemos([]);
    }
  }, [id]);

  useEffect(() => {
    load();
    loadLineItems();
    loadCostItems();
    loadPlaybooks();
    loadOpportunityDemos();
  }, [load, loadLineItems, loadCostItems, loadPlaybooks, loadOpportunityDemos]);

  async function togglePlaybookStep(playbookId: string, stepIndex: number, currentlyChecked: boolean) {
    if (!id) return;
    try {
      await apiClient.post(`/opportunities/${id}/playbooks/${playbookId}/progress`, {
        stepIndex,
        checked: !currentlyChecked,
      });
      loadPlaybooks();
    } catch {
      // ignore
    }
  }

  async function addLineItem() {
    if (!id || !newLine.unitPrice) return;
    try {
      await apiClient.post(`/opportunities/${id}/line-items`, {
        revenueType: newLine.revenueType,
        description: newLine.description || null,
        quantity: parseInt(newLine.quantity) || 1,
        unitPrice: parseFloat(newLine.unitPrice),
        recurrence: newLine.recurrence,
        recurrenceMonths: 0,
      });
      setNewLine({ revenueType: 'LICENSE', description: '', quantity: '1', unitPrice: '', recurrence: 'ONE_TIME' });
      loadLineItems();
      load();
    } catch {
      showToast('Failed to add line item');
    }
  }

  async function deleteLineItem(itemId: string) {
    if (!id) return;
    try {
      await apiClient.delete(`/opportunities/${id}/line-items/${itemId}`);
      loadLineItems();
      load();
    } catch {
      showToast('Failed to delete line item');
    }
  }

  async function addCostItem() {
    if (!id || !newCost.amount || !newCost.description) return;
    try {
      await apiClient.post(`/opportunities/${id}/cost-items`, {
        costCategory: newCost.costCategory,
        description: newCost.description,
        amount: parseFloat(newCost.amount),
        quantity: parseInt(newCost.quantity) || 1,
        recurrence: newCost.recurrence,
        recurrenceMonths: parseInt(newCost.recurrenceMonths) || 0,
        vendor: newCost.vendor || null,
      });
      setNewCost({
        costCategory: 'PURCHASE',
        description: '',
        amount: '',
        quantity: '1',
        recurrence: 'ONE_TIME',
        recurrenceMonths: '',
        vendor: '',
      });
      loadCostItems();
    } catch {
      showToast('Failed to add cost item');
    }
  }

  async function deleteCostItem(itemId: string) {
    if (!id) return;
    try {
      await apiClient.delete(`/opportunities/${id}/cost-items/${itemId}`);
      loadCostItems();
    } catch {
      showToast('Failed to delete cost item');
    }
  }

  function startEditLine(item: LineItem) {
    setEditingLineId(item.id);
    setEditLineDraft({
      revenueType: item.revenueType,
      description: item.description ?? '',
      quantity: String(item.quantity),
      unitPrice: String(item.unitPrice),
      recurrence: item.recurrence,
      recurrenceMonths: String(item.recurrenceMonths),
    });
  }

  function cancelEditLine() {
    setEditingLineId(null);
    setEditLineDraft(null);
  }

  async function saveEditLine() {
    if (!id || !editingLineId || !editLineDraft) return;
    try {
      await apiClient.patch(`/opportunities/${id}/line-items/${editingLineId}`, {
        revenueType: editLineDraft.revenueType,
        description: editLineDraft.description || null,
        quantity: parseInt(editLineDraft.quantity) || 1,
        unitPrice: parseFloat(editLineDraft.unitPrice) || 0,
        recurrence: editLineDraft.recurrence,
        recurrenceMonths: parseInt(editLineDraft.recurrenceMonths) || 0,
      });
      cancelEditLine();
      loadLineItems();
      load();
    } catch {
      showToast('Failed to update line item');
    }
  }

  function startEditCost(item: CostItem) {
    setEditingCostId(item.id);
    setEditCostDraft({
      costCategory: item.costCategory,
      description: item.description,
      amount: String(item.amount),
      quantity: String(item.quantity),
      recurrence: item.recurrence,
      recurrenceMonths: String(item.recurrenceMonths),
      vendor: item.vendor ?? '',
    });
  }

  function cancelEditCost() {
    setEditingCostId(null);
    setEditCostDraft(null);
  }

  async function saveEditCost() {
    if (!id || !editingCostId || !editCostDraft) return;
    try {
      await apiClient.patch(`/opportunities/${id}/cost-items/${editingCostId}`, {
        costCategory: editCostDraft.costCategory,
        description: editCostDraft.description,
        amount: parseFloat(editCostDraft.amount) || 0,
        quantity: parseInt(editCostDraft.quantity) || 1,
        recurrence: editCostDraft.recurrence,
        recurrenceMonths: parseInt(editCostDraft.recurrenceMonths) || 0,
        vendor: editCostDraft.vendor || null,
      });
      cancelEditCost();
      loadCostItems();
    } catch {
      showToast('Failed to update cost item');
    }
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function advance(stageId: string, stageName: string) {
    if (!id) return;
    // Open the modal — it pre-flights the gate check, lets the rep
    // fill missing required fields, and only then posts /advance.
    setAdvancing({ stageId, stageName });
  }

  async function submitClose() {
    if (!id || !closing) return;
    if (closing === 'LOST' && !closeReasonCode) return;
    const body: Record<string, string> = { outcome: closing };
    if (closing === 'LOST') {
      body.closeReasonCode = closeReasonCode;
      body.closeReasonCategory = closeReasonCategory;
      body.primaryReason = closeReason.trim() || closeReasonCode;
      if (closePrimaryCategory.trim()) body.primaryReasonCategory = closePrimaryCategory.trim();
      if (closePrimarySubcategory.trim()) body.primaryReasonSubcategory = closePrimarySubcategory.trim();
    }
    if (closeNotes.trim()) body.closeReasonNotes = closeNotes.trim();
    try {
      await apiClient.post(`/opportunities/${id}/close`, body);
      setClosing(null);
      setCloseReason('');
      setCloseReasonCode('');
      setCloseReasonCategory('');
      setCloseNotes('');
      setClosePrimaryCategory('');
      setClosePrimarySubcategory('');
      showToast(`Marked as ${closing}`);
      load();
    } catch {
      showToast('Failed to close opportunity');
    }
  }

  // Story 76.1 — load and open pre-meeting brief
  async function openBrief() {
    if (!id) return;
    try {
      const { data } = await apiClient.get<PreMeetingBrief>(`/opportunities/${id}/pre-meeting-brief`);
      setBrief(data);
      setBriefOpen(true);
    } catch {
      showToast(t('preMeetingBrief.title'));
    }
  }

  function copyBriefToClipboard() {
    if (!brief) return;
    const text = [
      `# ${brief.opportunityName}`,
      brief.accountName ? `${t('preMeetingBrief.account')}: ${brief.accountName}` : '',
      `${t('preMeetingBrief.deal_value')}: ${brief.currency} ${parseFloat(brief.dealValue).toLocaleString()}`,
      brief.expectedCloseDate ? `${t('preMeetingBrief.expected_close')}: ${brief.expectedCloseDate}` : '',
      brief.currentStageName ? `${t('preMeetingBrief.stage')}: ${brief.currentStageName}` : '',
      '',
      `## ${t('preMeetingBrief.talking_points')}`,
      ...brief.talkingPoints.map((p) => `- ${p}`),
      '',
      `## ${t('preMeetingBrief.recent_comms')}`,
      ...(brief.recentCommunications.length > 0
        ? brief.recentCommunications.map((c) => `- ${c}`)
        : [t('preMeetingBrief.no_comms')]),
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setBriefCopied(true);
      setTimeout(() => setBriefCopied(false), 2000);
    });
  }

  // Story 76.2 — submit meeting transcript for processing
  async function submitMeetingSummary() {
    if (!id || !transcript.trim()) return;
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const { data } = await apiClient.post<MeetingSummaryResult>(
        `/opportunities/${id}/meeting-summary`,
        { transcript: transcript.trim(), meetingType, meetingTitle: meetingTitle || undefined },
      );
      setSummaryResult(data);
      setTranscript('');
      setMeetingType('OTHER');
      setMeetingTitle('');
    } catch {
      setSummaryError(t('meetingSummary.error'));
    } finally {
      setSummaryLoading(false);
    }
  }

  if (!detail) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  }

  const s = detail.summary;
  const currentIdx = stages.findIndex((st) => st.id === s.currentStageId);

  return (
    <div className="space-y-6">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 shadow-lg text-sm">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link
            to="/opportunities"
            className="text-xs text-muted-foreground hover:underline"
          >
            &larr; {t('nav.opportunities')}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold">{s.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {detail.accountName && <span>{detail.accountName}</span>}
            {detail.contactName && <span>· {detail.contactName}</span>}
            {detail.campaignName && <span>· {detail.campaignName}</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={openBrief}>
            {t('preMeetingBrief.button')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setSummaryOpen(true); setSummaryResult(null); }}>
            {t('meetingSummary.button')}
          </Button>
          {/* 114.3 — Create demo CTA. The Demos tab itself ships in Phase 6. */}
          <RequirePermission permission="demo.create.basic">
            <Button size="sm" variant="secondary" onClick={() => setDemoSheetOpen(true)}>
              {t('demos.list.new')}
            </Button>
          </RequirePermission>
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              STATUS_BADGE[s.status] ?? 'bg-muted text-foreground'
            }`}
          >
            {s.status}
          </span>
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              FORECAST_BADGE[s.forecastClassification] ?? 'bg-muted text-foreground'
            }`}
          >
            {s.forecastClassification}
          </span>
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard
          label="Deal Value"
          value={`${s.currency} ${parseFloat(s.estimatedDealValue).toLocaleString()}`}
        />
        <MetricCard label="Progress" value={`${s.progressPercent}%`} />
        <MetricCard
          label="Expected Close"
          value={s.expectedCloseDate ?? '—'}
        />
        <MetricCard
          label="Competitors"
          value={
            s.competitorNames.length > 0
              ? s.competitorNames.join(', ')
              : 'None'
          }
        />
      </div>

      {/* Story 60.2 + 60.4 — A11 + A12 AI insights */}
      <OpportunityInsightsCard opportunityId={detail.summary.id} />

      {/* Gap 02: MEDDIC qualification panel */}
      <MEDDICPanel summary={s} opportunityId={s.id} />

      {/* Contacts on this opportunity's account */}
      {s.accountId && (
        <div className="rounded-lg border bg-card p-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Contacts
            </h2>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {accountContacts.length}
            </span>
          </div>
          {accountContacts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No contacts on this account yet — add one from the account page.
            </p>
          ) : (
            <ul className="divide-y">
              {accountContacts.map((c) => {
                const isPrimary = c.id === s.primaryContactId;
                return (
                  <li
                    key={c.id}
                    className="flex items-center justify-between gap-3 py-2"
                  >
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        {c.firstName} {c.lastName}
                        {isPrimary && (
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                            Primary
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {c.title ?? '—'}
                        {c.email ? ` · ${c.email}` : ''}
                        {c.phone ? ` · ${c.phone}` : ''}
                      </div>
                    </div>
                    {isPrimary ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPrimaryContact(null)}
                      >
                        Clear primary
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPrimaryContact(c.id)}
                      >
                        Set as primary
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Revenue Line Items */}
      <div className="rounded-lg border bg-card">
        <button
          onClick={() => setLineOpen(!lineOpen)}
          className="flex w-full items-center justify-between px-6 py-4 text-left"
        >
          <div className="flex items-center gap-2">
            {lineOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Revenue Line Items
            </h2>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {lineItems.length}
            </span>
          </div>
        </button>
        {lineOpen && (
          <div className="border-t px-6 pb-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Revenue Type</th>
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Unit Price</th>
                    <th className="px-3 py-2">Recurrence</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {lineItems.map((item) => (
                    editingLineId === item.id && editLineDraft ? (
                      <tr key={item.id} className="bg-amber-950/40">
                        <td className="px-3 py-2">
                          <select
                            value={editLineDraft.revenueType}
                            onChange={(e) => setEditLineDraft({ ...editLineDraft, revenueType: e.target.value })}
                            className="h-8 rounded-md border bg-background px-2 text-xs"
                          >
                            {REVENUE_TYPES.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            value={editLineDraft.description}
                            onChange={(e) => setEditLineDraft({ ...editLineDraft, description: e.target.value })}
                            className="h-8 text-xs"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            value={editLineDraft.quantity}
                            onChange={(e) => setEditLineDraft({ ...editLineDraft, quantity: e.target.value })}
                            className="h-8 w-16 text-right text-xs"
                            min={1}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            value={editLineDraft.unitPrice}
                            onChange={(e) => setEditLineDraft({ ...editLineDraft, unitPrice: e.target.value })}
                            className="h-8 w-24 text-right text-xs"
                            step="0.01"
                            min={0}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={editLineDraft.recurrence}
                            onChange={(e) => setEditLineDraft({ ...editLineDraft, recurrence: e.target.value })}
                            className="h-8 rounded-md border bg-background px-2 text-xs"
                          >
                            {LINE_RECURRENCES.map((r) => (
                              <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2" />
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <Button size="sm" className="h-7 px-2 text-xs" onClick={saveEditLine}>Save</Button>
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={cancelEditLine}>Cancel</Button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr
                        key={item.id}
                        className="cursor-pointer hover:bg-muted/30"
                        onClick={() => startEditLine(item)}
                        title="Click to edit"
                      >
                        <td className="px-3 py-2">
                          <span className="rounded bg-blue-950/40 px-1.5 py-0.5 text-xs font-medium text-blue-300">
                            {item.revenueType}
                          </span>
                        </td>
                        <td className="px-3 py-2">{item.description ?? '—'}</td>
                        <td className="px-3 py-2 text-right">{item.quantity}</td>
                        <td className="px-3 py-2 text-right">
                          {parseFloat(String(item.unitPrice)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2">{item.recurrence.replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2 text-right font-medium">
                          {parseFloat(String(item.totalValue)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                            onClick={() => deleteLineItem(item.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    )
                  ))}
                  {lineItems.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                        No line items yet.
                      </td>
                    </tr>
                  )}
                  {/* Add line item row */}
                  <tr className="bg-muted/30">
                    <td className="px-3 py-2">
                      <select
                        value={newLine.revenueType}
                        onChange={(e) => setNewLine({ ...newLine, revenueType: e.target.value })}
                        className="h-8 rounded-md border bg-background px-2 text-xs"
                      >
                        {REVENUE_TYPES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        value={newLine.description}
                        onChange={(e) => setNewLine({ ...newLine, description: e.target.value })}
                        placeholder="Description"
                        className="h-8 text-xs"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        value={newLine.quantity}
                        onChange={(e) => setNewLine({ ...newLine, quantity: e.target.value })}
                        className="h-8 w-16 text-right text-xs"
                        min={1}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        value={newLine.unitPrice}
                        onChange={(e) => setNewLine({ ...newLine, unitPrice: e.target.value })}
                        placeholder="0.00"
                        className="h-8 w-24 text-right text-xs"
                        step="0.01"
                        min={0}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={newLine.recurrence}
                        onChange={(e) => setNewLine({ ...newLine, recurrence: e.target.value })}
                        className="h-8 rounded-md border bg-background px-2 text-xs"
                      >
                        {LINE_RECURRENCES.map((r) => (
                          <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2" />
                    <td className="px-3 py-2">
                      <Button
                        size="sm"
                        className="h-7 gap-1 text-xs"
                        onClick={addLineItem}
                        disabled={!newLine.unitPrice}
                      >
                        <Plus className="h-3 w-3" />
                        Add
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Revenue total */}
            {lineItems.length > 0 && (
              <div className="mt-3 flex justify-end border-t pt-3">
                <div className="text-sm">
                  <span className="text-muted-foreground">Revenue Total: </span>
                  <span className="font-semibold">
                    {lineItems
                      .reduce((sum, li) => sum + parseFloat(String(li.totalValue)), 0)
                      .toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cost Items — only rendered when the endpoint is accessible */}
      {costVisible && (
        <div className="rounded-lg border bg-card">
          <button
            onClick={() => setCostOpen(!costOpen)}
            className="flex w-full items-center justify-between px-6 py-4 text-left"
          >
            <div className="flex items-center gap-2">
              {costOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Cost Items
              </h2>
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {costItems.length}
              </span>
            </div>
          </button>
          {costOpen && (
            <div className="border-t px-6 pb-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">Category</th>
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2">Recurrence</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2">Vendor</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {costItems.map((item) => (
                      editingCostId === item.id && editCostDraft ? (
                        <tr key={item.id} className="bg-amber-950/40">
                          <td className="px-3 py-2">
                            <select
                              value={editCostDraft.costCategory}
                              onChange={(e) => setEditCostDraft({ ...editCostDraft, costCategory: e.target.value })}
                              className="h-8 rounded-md border bg-background px-2 text-xs"
                            >
                              {COST_CATEGORIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              value={editCostDraft.description}
                              onChange={(e) => setEditCostDraft({ ...editCostDraft, description: e.target.value })}
                              className="h-8 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              value={editCostDraft.quantity}
                              onChange={(e) => setEditCostDraft({ ...editCostDraft, quantity: e.target.value })}
                              className="h-8 w-16 text-right text-xs"
                              min={1}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              value={editCostDraft.amount}
                              onChange={(e) => setEditCostDraft({ ...editCostDraft, amount: e.target.value })}
                              className="h-8 w-24 text-right text-xs"
                              step="0.01"
                              min={0}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={editCostDraft.recurrence}
                              onChange={(e) => setEditCostDraft({ ...editCostDraft, recurrence: e.target.value })}
                              className="h-8 rounded-md border bg-background px-2 text-xs"
                            >
                              {COST_RECURRENCES.map((r) => (
                                <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2" />
                          <td className="px-3 py-2">
                            <Input
                              value={editCostDraft.vendor}
                              onChange={(e) => setEditCostDraft({ ...editCostDraft, vendor: e.target.value })}
                              className="h-8 text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex gap-1">
                              <Button size="sm" className="h-7 px-2 text-xs" onClick={saveEditCost}>Save</Button>
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={cancelEditCost}>Cancel</Button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <tr
                          key={item.id}
                          className="cursor-pointer hover:bg-muted/30"
                          onClick={() => startEditCost(item)}
                          title="Click to edit"
                        >
                          <td className="px-3 py-2">
                            <span className="rounded bg-amber-950/40 px-1.5 py-0.5 text-xs font-medium text-amber-300">
                              {item.costCategory}
                            </span>
                          </td>
                          <td className="px-3 py-2">{item.description}</td>
                          <td className="px-3 py-2 text-right">{item.quantity}</td>
                          <td className="px-3 py-2 text-right">
                            {parseFloat(String(item.amount)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-3 py-2">{item.recurrence.replace(/_/g, ' ')}</td>
                          <td className="px-3 py-2 text-right font-medium">
                            {parseFloat(String(item.totalValue)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-3 py-2">{item.vendor ?? '—'}</td>
                          <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                              onClick={() => deleteCostItem(item.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      )
                    ))}
                    {costItems.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                          No cost items yet.
                        </td>
                      </tr>
                    )}
                    {/* Add cost item row */}
                    <tr className="bg-muted/30">
                      <td className="px-3 py-2">
                        <select
                          value={newCost.costCategory}
                          onChange={(e) => setNewCost({ ...newCost, costCategory: e.target.value })}
                          className="h-8 rounded-md border bg-background px-2 text-xs"
                        >
                          {COST_CATEGORIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          value={newCost.description}
                          onChange={(e) => setNewCost({ ...newCost, description: e.target.value })}
                          placeholder="Description"
                          className="h-8 text-xs"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          value={newCost.quantity}
                          onChange={(e) => setNewCost({ ...newCost, quantity: e.target.value })}
                          className="h-8 w-16 text-right text-xs"
                          min={1}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          value={newCost.amount}
                          onChange={(e) => setNewCost({ ...newCost, amount: e.target.value })}
                          placeholder="0.00"
                          className="h-8 w-24 text-right text-xs"
                          step="0.01"
                          min={0}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={newCost.recurrence}
                          onChange={(e) => setNewCost({ ...newCost, recurrence: e.target.value })}
                          className="h-8 rounded-md border bg-background px-2 text-xs"
                        >
                          {COST_RECURRENCES.map((r) => (
                            <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2" />
                      <td className="px-3 py-2">
                        <Input
                          value={newCost.vendor}
                          onChange={(e) => setNewCost({ ...newCost, vendor: e.target.value })}
                          placeholder="Vendor (optional)"
                          className="h-8 text-xs"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          size="sm"
                          className="h-7 gap-1 text-xs"
                          onClick={addCostItem}
                          disabled={!newCost.amount || !newCost.description}
                        >
                          <Plus className="h-3 w-3" />
                          Add
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Cost totals + Margin */}
              {(costItems.length > 0 || lineItems.length > 0) && (
                <div className="mt-3 flex justify-end gap-6 border-t pt-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Costs: </span>
                    <span className="font-semibold">
                      {costItems
                        .reduce((sum, ci) => sum + parseFloat(String(ci.totalValue)), 0)
                        .toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Margin: </span>
                    <span className="font-semibold">
                      {(
                        lineItems.reduce((sum, li) => sum + parseFloat(String(li.totalValue)), 0) -
                        costItems.reduce((sum, ci) => sum + parseFloat(String(ci.totalValue)), 0)
                      ).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {detail.needsStageReview && (
        <div className="rounded-lg border border-amber-700 bg-amber-950/40 px-4 py-2 text-sm text-amber-200">
          This opportunity has been flagged for stage review.
        </div>
      )}

      {/* Stage progression */}
      <div className="rounded-lg border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Pipeline Stage
            </h2>
            <p className="text-lg font-medium">
              {detail.currentStageName}
              <span className="ml-2 text-sm text-muted-foreground">
                ({detail.templateName})
              </span>
            </p>
          </div>
          <div className="text-right text-sm text-muted-foreground">
            {s.progressPercent}%
          </div>
        </div>

        {/* Stage progress bar */}
        <div className="mb-4 flex gap-1">
          {stages.map((stage, idx) => (
            <div
              key={stage.id}
              className={`h-2 flex-1 rounded-full transition-colors ${
                idx <= currentIdx ? 'bg-primary' : 'bg-muted'
              }`}
              title={`${stage.order}. ${stage.name} (${stage.percentageWeight}%)`}
            />
          ))}
        </div>

        {s.status === 'OPEN' && (
          <div className="flex flex-wrap gap-2">
            {stages.map((stage) => {
              const isCurrent = stage.id === s.currentStageId;
              return (
                <Button
                  key={stage.id}
                  size="sm"
                  variant={isCurrent ? 'default' : 'outline'}
                  disabled={isCurrent}
                  onClick={() => advance(stage.id, stage.name)}
                >
                  {stage.order}. {stage.name}
                  {stage.isGate && ' 🔒'}
                </Button>
              );
            })}
            <div className="ml-auto flex gap-2">
              {/* Enroll in sequence — story 61.4 */}
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  const res = await apiClient.get<{ id: string; name: string }[]>('/api/v1/sequences');
                  setSequences(res.data);
                  if (res.data.length > 0) setSelectedSequenceId(res.data[0].id);
                  setEnrollOpen(true);
                }}
              >
                {t('sequences.enrollment.enroll_btn')}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setClosing('WON')}
              >
                Close Won
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setClosing('LOST')}
              >
                Close Lost
              </Button>
            </div>
          </div>
        )}
      </div>

      {advancing && id && (
        <StageAdvanceModal
          opportunityId={id}
          targetStageId={advancing.stageId}
          targetStageName={advancing.stageName}
          onClose={() => setAdvancing(null)}
          onAdvanced={() => {
            const name = advancing.stageName;
            setAdvancing(null);
            showToast(`Advanced to ${name}`);
            load();
          }}
        />
      )}

      {/* Sequence enrollment modal — story 61.4 */}
      {enrollOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-lg bg-card p-6 shadow-lg space-y-4">
            <h2 className="text-lg font-semibold">{t('sequences.enrollment.title')}</h2>
            <div>
              <label className="block text-sm font-medium mb-1">{t('sequences.enrollment.select')}</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={selectedSequenceId}
                onChange={(e) => setSelectedSequenceId(e.target.value)}
              >
                {sequences.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEnrollOpen(false)}>
                {t('sequences.enrollment.cancel')}
              </Button>
              <Button
                size="sm"
                disabled={enrolling || !selectedSequenceId}
                onClick={async () => {
                  if (!id || !detail) return;
                  setEnrolling(true);
                  try {
                    await apiClient.post(`/api/v1/sequences/${selectedSequenceId}/enroll`, {
                      recipients: [{ recipientType: 'LEAD', recipientId: id }],
                    });
                    setEnrollOpen(false);
                    setToast(t('sequences.enrollment.success'));
                    setTimeout(() => setToast(null), 3000);
                  } finally {
                    setEnrolling(false);
                  }
                }}
              >
                {t('sequences.enrollment.enroll_btn')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Close dialog */}
      {closing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold">
              Close as {closing}
            </h3>
            {closing === 'LOST' && (
              <>
                <div className="mb-3 space-y-1">
                  <label className="text-sm font-medium">
                    {t('closeReason.label')} <span className="text-red-500">*</span>
                  </label>
                  <CloseReasonPicker
                    value={closeReasonCode}
                    onChange={(code, cat) => {
                      setCloseReasonCode(code);
                      setCloseReasonCategory(cat);
                    }}
                    allowDataQuality={allowDataQuality}
                  />
                  {closeReasonCategory === 'DATA_QUALITY' && (
                    <p className="text-xs text-amber-600">
                      {t('closeReason.dq_warning')}
                    </p>
                  )}
                </div>
                <div className="mb-3 space-y-1">
                  <label className="text-xs text-muted-foreground">
                    {t('closeReason.optional_text')}
                  </label>
                  <Input
                    value={closeReason}
                    onChange={(e) => setCloseReason(e.target.value)}
                    placeholder={t('closeReason.optional_placeholder')}
                  />
                </div>
                <div className="mb-3 grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Categoría primaria</label>
                    <Input
                      value={closePrimaryCategory}
                      onChange={(e) => setClosePrimaryCategory(e.target.value)}
                      placeholder="e.g. PRICE"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Subcategoría</label>
                    <Input
                      value={closePrimarySubcategory}
                      onChange={(e) => setClosePrimarySubcategory(e.target.value)}
                      placeholder="e.g. TOTAL_COST"
                    />
                  </div>
                </div>
              </>
            )}
            <div className="mb-4 space-y-1">
              <label className="text-sm font-medium">Notes</label>
              <textarea
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                placeholder="Optional notes for the review..."
                className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setClosing(null);
                  setCloseReason('');
                  setCloseNotes('');
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant={closing === 'WON' ? 'default' : 'destructive'}
                disabled={closing === 'LOST' && !closeReasonCode}
                onClick={submitClose}
              >
                Confirm {closing}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Playbook checklists */}
      {playbooks.length > 0 && (
        <div className="rounded-lg border bg-card p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('playbook.sidebar_title')}
          </h2>
          <div className="space-y-6">
            {playbooks.map((pb) => {
              const steps = pb.steps ?? [];
              const progress = pb.progress ?? [];
              const total = steps.length;
              const done = progress.filter((p) => p.checked).length;
              return (
                <div key={pb.id}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium">{pb.name}</h3>
                    <span className="text-xs text-muted-foreground">{done}/{total}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1.5 mb-3">
                    <div
                      className="bg-primary h-1.5 rounded-full transition-all"
                      style={{ width: total > 0 ? `${(done / total) * 100}%` : '0%' }}
                    />
                  </div>
                  <div className="space-y-2">
                    {steps.map((step, idx) => {
                      const prog = progress.find((p) => p.stepIndex === idx);
                      const checked = prog?.checked ?? false;
                      return (
                        <button
                          key={idx}
                          onClick={() => togglePlaybookStep(pb.id, idx, checked)}
                          className="flex items-start gap-3 w-full text-left hover:bg-muted/40 px-2 py-1.5 rounded-md transition-colors"
                        >
                          {checked ? (
                            <CheckSquare className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                          ) : (
                            <Square className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
                          )}
                          <div>
                            <p className={`text-sm ${checked ? 'line-through text-muted-foreground' : ''}`}>
                              {step.title}
                            </p>
                            {step.suggestedAction && !checked && (
                              <p className="text-xs text-muted-foreground mt-0.5">{step.suggestedAction}</p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Agent Insights */}
      <AgentInsights targetType="Opportunity" targetId={s.id} />

      {/* AI needs-assessment snapshot (epic 126) */}
      <SnapshotPanel entityType="OPPORTUNITY" entityId={s.id} />

      {/* Attachments */}
      <AttachmentPanel entityType="OPPORTUNITY" entityId={s.id} />

      {/* 114.6 — Demos tab: demos linked to this opportunity */}
      <div className="rounded-lg border bg-card p-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('opportunity.tabs.demos')}
          </h2>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {opportunityDemos.length}
            </span>
            <RequirePermission permission="demo.create.basic">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDemoSheetOpen(true)}
              >
                + {t('demos.list.new')}
              </Button>
            </RequirePermission>
          </div>
        </div>
        {opportunityDemos.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('demos.list.empty')}</p>
        ) : (
          <ul className="divide-y">
            {opportunityDemos.map((demo) => (
              <li key={demo.id} className="flex items-center justify-between gap-3 py-2">
                <div>
                  <a
                    href={`/demos/${demo.id}`}
                    className="text-sm font-medium hover:underline"
                  >
                    {demo.businessName}
                  </a>
                  <span className="ml-2 text-xs text-muted-foreground capitalize">
                    {demo.industryKey}
                  </span>
                </div>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  {t(`demos.status.${demo.status === 'AWAITING_REFINEMENT' ? 'awaitingRefinement' : demo.status === 'READY_TO_SEND' ? 'readyToSend' : demo.status.toLowerCase()}`)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Unified Timeline */}
      <TimelinePanel entityType="opportunity" entityId={s.id} />

      {/* Stage History */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Stage History
        </h2>
        <div className="relative space-y-0">
          {detail.stageHistory.map((h, i) => {
            const stageName =
              stages.find((st) => st.id === h.toStageId)?.name ?? h.toStageId;
            const fromName = h.fromStageId
              ? stages.find((st) => st.id === h.fromStageId)?.name
              : null;
            return (
              <div key={i} className="relative flex gap-3 pb-4">
                <div className="flex flex-col items-center">
                  <div
                    className={`h-3 w-3 rounded-full ${
                      h.direction === 'FORWARD'
                        ? 'bg-emerald-500'
                        : h.direction === 'BACKWARD'
                          ? 'bg-amber-500'
                          : 'bg-blue-500'
                    }`}
                  />
                  {i < detail.stageHistory.length - 1 && (
                    <div className="mt-1 w-px flex-1 bg-border" />
                  )}
                </div>
                <div className="flex-1 pb-1">
                  <div className="text-sm font-medium">
                    {DIRECTION_LABEL[h.direction] ?? h.direction}
                    {fromName ? ` from ${fromName}` : ''} to{' '}
                    <span className="font-semibold">{stageName}</span>
                  </div>
                  {h.reason && (
                    <div className="text-xs text-muted-foreground">
                      {h.reason}
                    </div>
                  )}
                  {h.occurredAt && (
                    <div className="text-xs text-muted-foreground">
                      {new Date(h.occurredAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {detail.stageHistory.length === 0 && (
            <p className="text-sm text-muted-foreground">No history yet.</p>
          )}
        </div>
      </div>

      {/* Story 76.1 — Pre-meeting brief dialog */}
      {briefOpen && brief && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg border bg-card p-6 shadow-xl overflow-y-auto max-h-[90vh]">
            <h3 className="mb-4 text-lg font-semibold">{t('preMeetingBrief.title')}</h3>
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                {brief.accountName && (
                  <div><span className="text-muted-foreground">{t('preMeetingBrief.account')}: </span>{brief.accountName}</div>
                )}
                <div><span className="text-muted-foreground">{t('preMeetingBrief.deal_value')}: </span>{brief.currency} {parseFloat(brief.dealValue).toLocaleString()}</div>
                {brief.expectedCloseDate && (
                  <div><span className="text-muted-foreground">{t('preMeetingBrief.expected_close')}: </span>{brief.expectedCloseDate}</div>
                )}
                {brief.currentStageName && (
                  <div><span className="text-muted-foreground">{t('preMeetingBrief.stage')}: </span>{brief.currentStageName}</div>
                )}
              </div>

              <div>
                <div className="mb-1 font-medium text-muted-foreground uppercase tracking-wide text-xs">{t('preMeetingBrief.talking_points')}</div>
                <ul className="list-disc pl-4 space-y-1">
                  {brief.talkingPoints.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>

              <div>
                <div className="mb-1 font-medium text-muted-foreground uppercase tracking-wide text-xs">{t('preMeetingBrief.recent_comms')}</div>
                {brief.recentCommunications.length === 0 ? (
                  <p className="text-muted-foreground">{t('preMeetingBrief.no_comms')}</p>
                ) : (
                  <ul className="list-disc pl-4 space-y-1">
                    {brief.recentCommunications.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                )}
              </div>

              {brief.openOpportunities.length > 0 && (
                <div>
                  <div className="mb-1 font-medium text-muted-foreground uppercase tracking-wide text-xs">{t('preMeetingBrief.open_opps')}</div>
                  <ul className="space-y-1">
                    {brief.openOpportunities.map((o) => (
                      <li key={o.id} className="text-sm">
                        {o.name} — {o.currency} {parseFloat(o.estimatedDealValue).toLocaleString()}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={copyBriefToClipboard}>
                {briefCopied ? t('preMeetingBrief.copied') : t('preMeetingBrief.copy')}
              </Button>
              <Button size="sm" variant="default" onClick={() => setBriefOpen(false)}>
                {t('preMeetingBrief.close')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Story 76.2 — Meeting summary dialog */}
      {summaryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg border bg-card p-6 shadow-xl overflow-y-auto max-h-[90vh]">
            <h3 className="mb-4 text-lg font-semibold">{t('meetingSummary.title')}</h3>
            {!summaryResult ? (
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium">{t('meetingSummary.meeting_type_label')}</label>
                  <select
                    value={meetingType}
                    onChange={(e) => setMeetingType(e.target.value)}
                    className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  >
                    {MEETING_TYPES.map((mt) => (
                      <option key={mt} value={mt}>{mt.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">{t('meetingSummary.meeting_title_label')}</label>
                  <Input
                    value={meetingTitle}
                    onChange={(e) => setMeetingTitle(e.target.value)}
                    placeholder={t('meetingSummary.meeting_title_label')}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">{t('meetingSummary.transcript_label')}</label>
                  <textarea
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    placeholder={t('meetingSummary.transcript_placeholder')}
                    className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                    rows={8}
                  />
                </div>
                {summaryError && (
                  <p className="text-sm text-destructive">{summaryError}</p>
                )}
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setSummaryOpen(false)}>
                    {t('meetingSummary.close')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={submitMeetingSummary}
                    disabled={summaryLoading || !transcript.trim()}
                  >
                    {summaryLoading ? t('meetingSummary.submitting') : t('meetingSummary.submit')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4 text-sm">
                <h4 className="font-semibold">{t('meetingSummary.result_title')}</h4>
                <div>
                  <div className="mb-1 font-medium text-muted-foreground uppercase tracking-wide text-xs">{t('meetingSummary.summary_label')}</div>
                  <p className="whitespace-pre-line">{summaryResult.summary}</p>
                </div>
                {summaryResult.actionItems.length > 0 && (
                  <div>
                    <div className="mb-1 font-medium text-muted-foreground uppercase tracking-wide text-xs">{t('meetingSummary.action_items_label')}</div>
                    <ul className="list-decimal pl-4 space-y-1">
                      {summaryResult.actionItems.map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('meetingSummary.tasks_created', { count: summaryResult.tasksCreated })}
                    </p>
                  </div>
                )}
                <div>
                  <div className="mb-1 font-medium text-muted-foreground uppercase tracking-wide text-xs">{t('meetingSummary.next_action_label')}</div>
                  <p>{summaryResult.suggestedNextAction}</p>
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={() => { setSummaryOpen(false); setSummaryResult(null); }}>
                    {t('meetingSummary.close')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 114.3 — Demo creation sheet (Closer front door) */}
      <OpportunityCreateDemoSheet
        open={demoSheetOpen}
        onClose={() => setDemoSheetOpen(false)}
        opportunityId={id ?? ''}
        accountName={detail.accountName ?? ''}
      />
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

// Story 60.2 + 60.4 — A11 Revenue Prediction + A12 Deal Strategist
// surface on the opportunity. Reads the latest snapshot and exposes a
// "Run now" button that triggers fresh runs synchronously.

interface InsightSnapshot {
  payloadJson: string;
  confidence: number | null;
  reasoning: string | null;
  createdAt: string | null;
}

interface OpportunityInsights {
  revenuePrediction: InsightSnapshot | null;
  dealStrategy: InsightSnapshot | null;
}

function OpportunityInsightsCard({ opportunityId }: { opportunityId: string }) {
  const [data, setData] = useState<OpportunityInsights | null>(null);
  const [running, setRunning] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    apiClient
      .get<OpportunityInsights>(`/opportunities/${opportunityId}/insights`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ revenuePrediction: null, dealStrategy: null }));
  }, [opportunityId, refreshToken]);

  async function runNow() {
    setRunning(true);
    try {
      await apiClient.post(`/opportunities/${opportunityId}/insights/run`);
      setRefreshToken((x) => x + 1);
    } catch {
      // intentional — UI shows whatever the last snapshot was
    } finally {
      setRunning(false);
    }
  }

  const a11 = data?.revenuePrediction
    ? safeParse<{ closeProbability?: number; drivers?: string[]; rationale?: string }>(
        data.revenuePrediction.payloadJson,
      )
    : null;
  const a12 = data?.dealStrategy
    ? safeParse<{ action?: string; rationale?: string; urgency?: string }>(
        data.dealStrategy.payloadJson,
      )
    : null;

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          AI insights
        </h2>
        <Button size="sm" variant="outline" onClick={runNow} disabled={running}>
          {running ? 'Running…' : 'Run now'}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-md border-l-4 border-emerald-500 bg-emerald-950/40/40 px-4 py-3">
          <div className="text-xs uppercase text-emerald-200">Close probability (A11)</div>
          {a11 && a11.closeProbability !== undefined ? (
            <>
              <div className="mt-1 text-3xl font-bold">{a11.closeProbability}%</div>
              {a11.drivers && a11.drivers.length > 0 && (
                <ul className="mt-2 list-disc pl-4 text-xs text-muted-foreground">
                  {a11.drivers.slice(0, 3).map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              )}
              {data?.revenuePrediction?.createdAt && (
                <div className="mt-2 text-[10px] text-muted-foreground">
                  Last run {new Date(data.revenuePrediction.createdAt).toLocaleString()}
                </div>
              )}
            </>
          ) : (
            <div className="mt-1 text-sm text-muted-foreground">
              No prediction yet. Click Run now.
            </div>
          )}
        </div>

        <div className="rounded-md border-l-4 border-amber-500 bg-amber-950/40/40 px-4 py-3">
          <div className="text-xs uppercase text-amber-200">Next-best action (A12)</div>
          {a12 && a12.action ? (
            <>
              <div className="mt-1 text-sm font-semibold">{a12.action}</div>
              {a12.rationale && (
                <p className="mt-2 text-xs text-muted-foreground">{a12.rationale}</p>
              )}
              {a12.urgency && (
                <span className="mt-2 inline-block rounded-full bg-amber-900/40 px-2 py-0.5 text-[10px] font-medium text-amber-200">
                  Urgency: {a12.urgency}
                </span>
              )}
              {data?.dealStrategy?.createdAt && (
                <div className="mt-2 text-[10px] text-muted-foreground">
                  Last run {new Date(data.dealStrategy.createdAt).toLocaleString()}
                </div>
              )}
            </>
          ) : (
            <div className="mt-1 text-sm text-muted-foreground">
              No strategy yet. Click Run now.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function safeParse<T>(json: string): T | null {
  try {
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

function MEDDICPanel({ summary, opportunityId }: { summary: Summary; opportunityId: string }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    metrics: summary.meddicMetrics ?? '',
    decisionProcess: summary.meddicDecisionProcess ?? '',
    paperProcess: summary.meddicPaperProcess ?? '',
  });

  async function save() {
    setSaving(true);
    try {
      await apiClient.patch(`/opportunities/${opportunityId}/meddic`, {
        metrics: form.metrics || null,
        decisionProcess: form.decisionProcess || null,
        paperProcess: form.paperProcess || null,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  const healthColor =
    summary.aiHealthScore == null
      ? 'bg-muted text-muted-foreground'
      : summary.aiHealthScore >= 70
      ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
      : summary.aiHealthScore >= 40
      ? 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300'
      : 'bg-red-500/20 text-red-700 dark:text-red-300';

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          MEDDIC Qualification
        </h2>
        <div className="flex items-center gap-2">
          {summary.aiHealthScore != null && (
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${healthColor}`}>
              Health {summary.aiHealthScore}
            </span>
          )}
          {summary.aiCloseProbability != null && (
            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
              AI Close {summary.aiCloseProbability}%
            </span>
          )}
          <Button size="sm" variant="outline" onClick={() => setEditing((e) => !e)}>
            {editing ? 'Cancel' : 'Edit'}
          </Button>
        </div>
      </div>

      {summary.aiRiskFlags && summary.aiRiskFlags.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {summary.aiRiskFlags.map((flag) => (
            <span
              key={flag}
              className="rounded-full bg-red-500/15 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-300"
            >
              {flag}
            </span>
          ))}
        </div>
      )}

      {editing ? (
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Metrics (quantifiable value)
            </label>
            <textarea
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              rows={3}
              value={form.metrics}
              onChange={(e) => setForm({ ...form, metrics: e.target.value })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Decision Process
            </label>
            <textarea
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              rows={3}
              value={form.decisionProcess}
              onChange={(e) => setForm({ ...form, decisionProcess: e.target.value })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Paper Process / Legal
            </label>
            <textarea
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              rows={2}
              value={form.paperProcess}
              onChange={(e) => setForm({ ...form, paperProcess: e.target.value })}
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            { label: 'Metrics', value: summary.meddicMetrics },
            { label: 'Decision Process', value: summary.meddicDecisionProcess },
            { label: 'Paper Process', value: summary.meddicPaperProcess },
          ].map(({ label, value }) => (
            <div key={label}>
              <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
              <div className="mt-1 text-sm">
                {value ?? <span className="text-muted-foreground">—</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
